import { useEffect, useState } from 'react';

import clsxm from '@/lib/clsxm';
import type { LoginMethod } from '@/hooks/useLastUsedLoginMethod';

import LastUsedBadge from '@/components/LastUsedBadge';

import {
  buttonClass,
  FacebookGlyph,
  GoogleGlyph,
  Spinner,
  useAuthT,
} from './kit';

export type OAuthProvider = 'google' | 'facebook';

const PROVIDERS: Array<{
  provider: OAuthProvider;
  name: string;
  Glyph: typeof GoogleGlyph;
}> = [
  { provider: 'google', name: 'Google', Glyph: GoogleGlyph },
  { provider: 'facebook', name: 'Facebook', Glyph: FacebookGlyph },
];

/**
 * "Continue with Google / Facebook". Google comes first: it is how most
 * people sign up (85% of new accounts in Oct 2026). The pressed button keeps
 * a spinner while the browser leaves for the provider; it clears when an
 * error comes back, when the page returns from the back/forward cache, or
 * after 10 s.
 */
export default function OAuthButtons({
  onSelect,
  disabled,
  lastUsed,
  errorKey,
}: {
  onSelect: (provider: OAuthProvider) => void;
  disabled?: boolean;
  lastUsed?: LoginMethod;
  /** Changes whenever the form shows a new error. */
  errorKey?: unknown;
}) {
  const { t } = useAuthT();
  const [pending, setPending] = useState<OAuthProvider | null>(null);

  useEffect(() => {
    setPending(null);
  }, [errorKey]);

  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(() => setPending(null), 10000);
    return () => clearTimeout(timer);
  }, [pending]);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setPending(null);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  return (
    <div className='space-y-3'>
      {PROVIDERS.map(({ provider, name, Glyph }) => (
        <button
          key={provider}
          type='button'
          onClick={() => {
            setPending(provider);
            onSelect(provider);
          }}
          disabled={disabled || pending !== null}
          aria-busy={pending === provider || undefined}
          className={clsxm(
            buttonClass('secondary'),
            // The pressed button stays at full strength behind its spinner.
            pending === provider && 'disabled:opacity-100',
          )}
        >
          <span className='absolute start-4 flex h-5 w-5 items-center justify-center'>
            {pending === provider ? (
              <Spinner className='h-5 w-5 text-[#6f6f86] dark:text-[#a4a4bd]' />
            ) : (
              <Glyph className='h-5 w-5' />
            )}
          </span>
          <span>{t('Continue with {{provider}}', { provider: name })}</span>
          {lastUsed === provider && <LastUsedBadge label={t('Last used')} />}
        </button>
      ))}
    </div>
  );
}
